/**
 * @file     ToastProvider.tsx
 * @purpose  App-wide transient toast system. Provider + useToast hook; stacks
 *           up to 3, auto-dismisses (4s success/info, 8s error). Mounted once
 *           at the root layout so every surface (buyer, partner, admin) can
 *           raise a toast. Nesting-safe: a nested provider detects an existing
 *           ancestor and passes through without a second viewport.
 */
'use client';

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { glassMaterial } from '@/design-system/tokens/glass';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import { useAppTheme } from '@/core/theme';
import { zLayers } from '@/design-system/tokens/zLayers';

export type ToastTone = 'success' | 'error' | 'info';
/** One inline action (Undo, Retry). Clicking it dismisses the toast. */
export type ToastAction = { label: string; onClick: () => void };
type Toast = { id: number; tone: ToastTone; message: string; action?: ToastAction };

export type ToastApi = { show: (message: string, tone?: ToastTone, action?: ToastAction) => void };

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside a ToastProvider');
  return ctx;
}

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  // Nesting guard: if a ToastProvider already exists above us, don't stand up a
  // second stack/viewport — just pass through so callers share one provider.
  const parent = useContext(ToastContext);
  const { theme } = useAppTheme();
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, tone: ToastTone = 'info', action?: ToastAction) => {
      const id = nextId++;
      setToasts((prev) => [...prev.slice(-2), { id, tone, message, action }]);
      // An action needs time to be read and reached; error already has it.
      window.setTimeout(() => dismiss(id), tone === 'error' ? 8000 : action ? 5000 : 4000);
    },
    [dismiss],
  );

  if (parent) return <>{children}</>;

  const toneColor = (tone: ToastTone) =>
    tone === 'success'
      ? theme.semanticColors.statusSuccess
      : tone === 'error'
        ? theme.semanticColors.statusError
        : theme.semanticColors.interactivePrimary;

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <Box
        aria-live="polite"
        sx={{
          position: 'fixed',
          // 8px above the bottom-slot holder when one is up (BottomSlot sets the var).
          bottom: 'max(20px, calc(var(--rlta-bottom-slot-top, 0px) + 8px))',
          right: 20,
          zIndex: zLayers.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {toasts.map((t) => (
          <Box
            key={t.id}
            role="status"
            onClick={() => dismiss(t.id)}
            sx={{
              minWidth: 260,
              maxWidth: 380,
              px: 2,
              py: 1.5,
              borderRadius: `${theme.layout.radius.md}px`,
              ...glassMaterial('regular'),
              borderLeft: `3px solid ${toneColor(t.tone)}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 13, color: theme.semanticColors.textPrimary, flex: 1 }}>
              {t.message}
            </Typography>
            {t.action && (
              <ButtonBase
                onClick={(event) => {
                  event.stopPropagation();
                  t.action?.onClick();
                  dismiss(t.id);
                }}
                sx={{
                  flexShrink: 0,
                  px: 1,
                  mx: -1,
                  minHeight: 32,
                  borderRadius: `${theme.layout.radius.sm}px`,
                  fontSize: 13,
                  fontWeight: 600,
                  color: t.tone === 'error' ? theme.semanticColors.statusError : theme.semanticColors.textSecondary,
                  transition: `background-color ${theme.motion.durations.fast}ms ${theme.motion.easing.standard}`,
                  '&:hover': { backgroundColor: theme.semanticColors.interactiveHover },
                  '&:focus-visible': { outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`, outlineOffset: 2 },
                }}
              >
                {t.action.label}
              </ButtonBase>
            )}
          </Box>
        ))}
      </Box>
    </ToastContext.Provider>
  );
}
